import "react-toastify/dist/ReactToastify.css";

import { useEffect, useState } from "react";

import { getDefaultWallets, RainbowKitProvider } from "@rainbow-me/rainbowkit";
import { Spin } from "antd";
import axios from "axios";
import { ethers } from "ethers";
import Parse from "parse";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import { configureChains, createConfig, WagmiConfig, Chain } from "wagmi";
import { alchemyProvider } from "wagmi/providers/alchemy";
import { publicProvider } from "wagmi/providers/public";

import ClaimTopicsPage from "./components/ClaimTopicsPage.jsx";
import CreateClaimTopic from "./components/CreateClaimTopic.jsx";
import CreateDigitalId from "./components/CreateDigitalId.jsx";
import CreatePassword from "./components/CreatePasswordPage.jsx";
import CreateTrustedIssuer from "./components/CreateTrustedIssuer.jsx";
import CredentialsCodePage from "./components/CredentialsCodePage";
import DigitalIdentityDetailView from "./components/DigitalIdentityDetailPage.jsx";
import EditClaims from "./components/EditClaims.jsx";
import EditClaimsSummaryView from "./components/EditClaimsSummaryView.jsx";
import ForgotPasswordPage from "./components/ForgotPasswordPage.jsx";
import Home from "./components/Home.jsx";
import IdentitiesPage from "./components/IdentitiesPage.jsx";
import Layout from "./components/Layout";
import Login from "./components/LoginPage.jsx";
import NavBar from "./components/NavBar.jsx";
import Protected from "./components/Protected";
import TrustedIssuersPage from "./components/TrustedIssuersPage.jsx";
import ViewClaimTopic from "./components/ViewClaimTopic";
import { RoleContext } from "./context/RoleContext";
import BlockchainService from "./services/BlockchainService.js";
import parseInitialize from "./services/parseInitialize";
import AutoLogout from "./utils/AutoLogout";
import { WalletPreference } from "./utils/Constants.js";

const localhost: Chain = {
  id: 31337,
  name: "Localhost",
  network: "localhost",
  nativeCurrency: {
    decimals: 18,
    name: "Ethereum",
    symbol: "ETH",
  },
  rpcUrls: {
    default: {
      http: [process.env.REACT_APP_NETWORK_LOCALHOST || ""],
    },
    public: {
      http: [process.env.REACT_APP_NETWORK_LOCALHOST || ""],
    },
  },
  testnet: true,
};

const baseSep: Chain = {
  id: 84532,
  network: "base",
  name: "Base Sepolia",
  nativeCurrency: {
    name: "Base",
    symbol: "ETH",
    decimals: 18,
  },
  rpcUrls: {
    default: {
      http: [process.env.REACT_APP_NETWORK_BASE_SEPOLIA || ""],
    },
    public: {
      http: [process.env.REACT_APP_NETWORK_BASE_SEPOLIA || ""],
    },
  },
};

const optSep: Chain = {
  id: 11155420,
  network: "optimism",
  name: "Optimism Sepolia",
  nativeCurrency: {
    name: "OP Sepolia",
    symbol: "ETH",
    decimals: 18,
  },
  rpcUrls: {
    default: {
      http: [process.env.REACT_APP_NETWORK_OPTIMISM_SEPOLIA || ""],
    },
    public: {
      http: [process.env.REACT_APP_NETWORK_OPTIMISM_SEPOLIA || ""],
    },
  },
};

const base: Chain = {
  id: 8453,
  network: "base",
  name: "Base",
  nativeCurrency: {
    name: "Base",
    symbol: "ETH",
    decimals: 18,
  },
  rpcUrls: {
    default: {
      http: [process.env.REACT_APP_NETWORK_BASE || ""],
    },
    public: {
      http: [process.env.REACT_APP_NETWORK_BASE || ""],
    },
  },
};

const arbitrumSepolia: Chain = {
  id: 421614,
  network: "arbitrum-sepolia",
  name: "Arbitrum Sepolia",
  nativeCurrency: {
    name: "Arbitrum Sepolia",
    symbol: "ETH",
    decimals: 18,
  },
  rpcUrls: {
    default: {
      http: [process.env.REACT_APP_NETWORK_ARBITRUM_SEPOLIA || ""],
    },
    public: {
      http: [process.env.REACT_APP_NETWORK_ARBITRUM_SEPOLIA || ""],
    },
  },
};

const { chains, publicClient } = configureChains(
  [base, baseSep, localhost, optSep, arbitrumSepolia], // mainnet, polygon, optimism, arbitrum, zora,
  [alchemyProvider({ apiKey: "CSgNtTJ6_Clrf1zNjVp2j1ppfLE2-aVX" }), publicProvider()]
);

const { connectors } = getDefaultWallets({
  appName: "LL Testing",
  projectId: "ae575761a72370ab88834655acbba677",
  chains,
});

const wagmiConfig = createConfig({
  autoConnect: false,
  connectors,
  publicClient,
});

// Cap a promise so a hung dependency can't strand the caller. Parse.User.become can spin
// in a retry loop (the GET /parse/users/me case) and never resolve or reject; without this
// the app would sit on the initializing spinner forever and never render the login form.
const withTimeout = <T,>(p: Promise<T>, ms: number, label: string): Promise<T> =>
  Promise.race([p, new Promise<T>((_, reject) => setTimeout(() => reject(new Error(`${label} timed out after ${ms}ms`)), ms))]);

const validateToken = async (token: string) => {
  try {
    const response = await axios.get(`${process.env.REACT_APP_PARSE_SERVER_URL}/legacy-auth/validate`, {
      headers: {
        "x-parse-session-token": token,
      },
    });

    const data = response.data;
    return {
      valid: data?.valid || false,
      roles: data?.user?.roles || [],
      user: data?.user,
      walletPreference: data?.user?.walletPreference,
      dfnsToken: data?.dfnsToken,
    };
  } catch (error) {
    console.error("Error validating token:", error);
    return {
      valid: false,
      roles: [],
      walletPreference: "",
      dfnsToken: null,
    };
  }
};

function App() {
  const [isConnected, setIsConnected] = useState(false);
  const [blockchainService, setBlockchainService] = useState<BlockchainService | null>(null);
  const [user, setUser] = useState(null); // State to hold user
  const [role, setRole] = useState<any>([]);
  const [walletPreference, setWalletPreference] = useState<WalletPreference | null>(null);
  const [dfnsToken, setDfnsToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(true); // New state for initialization
  //let provider: ethers.providers.Provider | null = null;
  let [provider, setProvider] = useState<ethers.providers.Provider | null>(null);
  // Define the getToken function
  const getToken = async (request: any) => {
    try {
      const response = await axios.post(`${process.env.REACT_APP_PARSE_SERVER_URL}/legacy-auth/login`, request);
      const data = response.data;
      return {
        walletPreference: data?.user?.walletPreference,
        token: data?.access_token || "",
        jwt: data?.jwt || "",
        roles: data?.user?.roles || [],
        user: data?.user,
        dfnsToken: data?.dfns_token,
      };
    } catch (error) {
      console.log("Error during authentication:", error);
      return {
        walletPreference: "",
        token: "",
        jwt: "",
        roles: [],
        user: null,
        dfnsToken: null,
      };
    }
  };

  // Abstracted function to initialize BlockchainService
  const initializeBlockchainService = (provider: ethers.providers.Provider) => {
    console.log("Initializing BlockchainService...");
    const _blockchainService = new BlockchainService(
      provider,
      process.env.REACT_APP_HARDHAT_CONTRACT_ADDRESS || "",
      process.env.REACT_APP_HARDHAT_IDENTITY_FACTORY_ADDRESS || ""
    );
    setBlockchainService(_blockchainService);
  };

  // Define the onLogout function for email/password login
  const onLogoutEmailPassword = async () => {
    try {
      const token = localStorage.getItem("sessionToken");
      if (token) {
        // Invalidate the session token on the server
        await axios.post(
          `${process.env.REACT_APP_PARSE_SERVER_URL}/legacy-auth/logout`,
          {},
          {
            headers: {
              "x-parse-session-token": token,
            },
          }
        );
      }
    } catch (error) {
      console.error("Error during logout:", error);
      toast.error("Error during logout. Please try again.");
      return;
    }

    // Clear the Parse SDK's own cached session so a stale/destroyed
    // sessionToken can't be reused by a later Parse.User.become() race.
    await Parse.User.logOut().catch(() => {});

    // Reset client-side state
    setRole([]);
    setWalletPreference(null);
    setDfnsToken(null);
    setUser(null);
    setIsConnected(false);
    localStorage.removeItem("sessionToken");
    localStorage.removeItem("authJwt");
    localStorage.removeItem("tokenExpiration");
    setBlockchainService(null);

    toast.success("Logged out successfully.");
  };

  // Define the onLogin function (Username/Password Login)
  const onLogin = async (email: string, password: string) => {
    const { token, jwt, roles, walletPreference, user, dfnsToken } = await getToken({ email, password });

    const expirationTime = Date.now() + 60 * 30 * 1000; // 30m logout
    if (roles.length > 0) {
      // Store the session token AND the auth JWT BEFORE parseInitialize(),
      // because parseInitialize() reads `authJwt` from localStorage to install
      // the `Authorization: Bearer` header that the backend `withAuth` checks.
      // Storing it after (the previous order) left gated cloud calls such as
      // setUserStatus (identity move across tabs) tokenless → auth error.
      localStorage.setItem("sessionToken", token);
      localStorage.setItem("authJwt", jwt);
      localStorage.setItem("tokenExpiration", expirationTime.toString());
      await parseInitialize();

      // Confirm the caller's roles via the backend `whoami` cloud function.
      // This is the AUTHORITATIVE set the server's `withAuth` gates read
      // (re-derived from the JWT), not just the legacy-auth login payload —
      // so the UI's notion of the user's roles matches what the backend enforces.
      const ADMIN_UI_ROLES = ["CentralAuthority", "admin", "TrustedIssuer", "reviewer"];
      let resolvedRoles: string[] = roles;
      try {
        const who = await Parse.Cloud.run("whoami");
        resolvedRoles = who?.roles ?? [];
        console.log("whoami resolved roles:", resolvedRoles);
      } catch (err) {
        console.error("whoami failed; falling back to login roles:", err);
      }

      const hasAdminUiAccess = resolvedRoles.some((r) => ADMIN_UI_ROLES.includes(r));
      if (!hasAdminUiAccess) {
        toast.error("This account has no Admin UI access (needs CentralAuthority, admin, TrustedIssuer, or reviewer).");
        localStorage.removeItem("sessionToken");
        localStorage.removeItem("authJwt");
        localStorage.removeItem("tokenExpiration");
        return;
      }

      setRole([...resolvedRoles]);
      setUser(user);
      setDfnsToken(dfnsToken);
      setWalletPreference(walletPreference);
      setIsConnected(true);
      //const provider = await setupProvider();
      if (!provider) {
        console.error("❌ Failed to initialize provider.");
        toast.error("Could not initialize provider. Please try again.");
        return;
      }

      console.log("✅ Provider initialized:", provider);
      initializeBlockchainService(provider);
    } else {
      toast.error("Incorrect username / password");
    }
  };

  useEffect(() => {
    setupProvider().then(setProvider);
  }, []);

  async function setupProvider(): Promise<ethers.providers.Provider | null> {
    let newProvider: ethers.providers.Provider | null = null;
    console.log("🔍 Checking for wallet provider...");

    if (typeof window !== "undefined" && (window as any).ethereum) {
      try {
        newProvider = new ethers.providers.Web3Provider((window as any).ethereum);
      } catch (error) {
        console.error("❌ Wallet provider error:", error);
      }
    }

    if (!newProvider) {
      const rpcUrl = process.env.REACT_APP_RPC_URL;
      if (rpcUrl) {
        console.log("⚠️ No wallet detected. Using RPC provider instead:", rpcUrl);
        // Pinning a static network here skips ethers' automatic eth_chainId
        // detection call before every request — StaticJsonRpcProvider only
        // avoids re-detecting on every call if it already knows the network
        // up front.
        const chainId = Number(process.env.REACT_APP_HARDHAT_CHAIN_ID);
        newProvider = new ethers.providers.StaticJsonRpcProvider(rpcUrl, chainId ? { name: "unknown", chainId } : undefined);
        if (newProvider instanceof ethers.providers.StaticJsonRpcProvider) {
          // NOTE: setting `_pollingInterval` (the private field) directly, as
          // this used to do, does not go through ethers' `pollingInterval`
          // setter — and contract.on(...) listeners (registered right after
          // this in BlockchainService) turn `polling` back on regardless of
          // any `polling = false` set here. The interval actually used ends
          // up being whatever `_pollingInterval` holds when that happens; a
          // raw `Infinity` there gets coerced by setInterval into firing
          // near-instantly instead of "never" — which is what was silently
          // hammering eth_getLogs every fraction of a second. Set a real,
          // deliberately slow interval via the public setter instead.
          newProvider.pollingInterval = 30000;
        }
      }
    }

    if (newProvider) {
      setProvider(newProvider);
      return newProvider;
    }

    console.error("❌ No provider available! Please connect a wallet or set an RPC URL.");
    return null;
  }

  const restoreSession = async () => {
    const clearStoredSession = () => {
      localStorage.removeItem("sessionToken");
      localStorage.removeItem("authJwt");
      localStorage.removeItem("tokenExpiration");
    };
    try {
      const token = localStorage.getItem("sessionToken");
      if (token) {
        // Time-cap both awaits: a stale/unreachable session makes Parse.User.become hang
        // (never resolves), which would otherwise leave `initializing` true forever and
        // never render the login form. On timeout/failure we drop the bad token and fall
        // through to the login screen instead of stranding the app.
        await withTimeout(parseInitialize(), 8000, "parseInitialize");
        const { valid, roles, walletPreference, user, dfnsToken } = await withTimeout(validateToken(token), 8000, "validateToken");
        if (valid && roles.length > 0) {
          setRole(roles);
          setUser(user);
          setDfnsToken(dfnsToken);
          setWalletPreference(walletPreference);
          setIsConnected(true);
        } else {
          // Token is invalid or roles are empty
          clearStoredSession();
        }
      }
    } catch (e) {
      console.error("restoreSession failed; clearing stored session and showing login:", e);
      clearStoredSession();
    } finally {
      setInitializing(false);
    }
  };

  useEffect(() => {
    restoreSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle loading state when roles change
  useEffect(() => {
    if (role.length > 0) {
      setLoading(true);
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  }, [role]);

  useEffect(() => {
    if (provider && isConnected) {
      initializeBlockchainService(provider);
    }
  }, [provider, isConnected]);

  if (initializing) {
    return (
      <div className="z-50 h-screen w-screen flex justify-center items-center">
        <Spin />
      </div>
    );
  }

  return (
    <WagmiConfig config={wagmiConfig}>
      <RainbowKitProvider chains={chains}>
        <RoleContext.Provider value={{ role, setRole, walletPreference, setWalletPreference, dfnsToken, setDfnsToken, user, setUser }}>
          {/* Loading Spinner Overlay */}
          {loading && (
            <div className="z-50 h-screen w-screen overflow-hidden absolute top-0 left-0 flex justify-center items-center bg-[#00000040]">
              <Spin />
            </div>
          )}
          <Router>
            <AutoLogout />
            {/* Navigation Bar (Only visible when logged in) */}
            {role.length > 0 && (
              <div className={`topnav p-0`}>
                <NavBar onLogout={onLogoutEmailPassword} />
              </div>
            )}

            <Layout>
              <div className={`${role.length === 0 ? "p-0 -ml-4 overflow-hidden" : "content"}`}>
                <ToastContainer
                  position="top-right"
                  className="toast-background"
                  progressClassName="toast-progress-bar"
                  autoClose={4000}
                  closeOnClick
                  pauseOnHover
                />
                {/* Application Routes */}
                <Routes>
                  <Route
                    path="/"
                    element={
                      <Protected roles={role}>
                        <Home />
                      </Protected>
                    }
                  />
                  <Route path="/login" element={<Login onLogin={onLogin} />} />
                  <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                  <Route path="/create-password/:token" element={<CreatePassword service={blockchainService} />} />
                  <Route path="/credentials/code" element={<CredentialsCodePage />} />

                  <Route
                    path="/topics"
                    element={
                      <Protected roles={role}>
                        {blockchainService ? (
                          <ClaimTopicsPage service={blockchainService} />
                        ) : (
                          <div className="flex justify-center items-center h-full">
                            <Spin tip="Initializing service..." />
                          </div>
                        )}
                      </Protected>
                    }
                  />
                  <Route
                    path="/topics/create"
                    element={
                      <Protected roles={role}>
                        {blockchainService ? (
                          <CreateClaimTopic service={blockchainService} />
                        ) : (
                          <div className="flex justify-center items-center h-full">
                            <Spin tip="Initializing service..." />
                          </div>
                        )}
                      </Protected>
                    }
                  />
                  <Route
                    path="/topics/:topicId"
                    element={
                      <Protected roles={role}>
                        {blockchainService ? (
                          <ViewClaimTopic service={blockchainService} />
                        ) : (
                          <div className="flex justify-center items-center h-full">
                            <Spin tip="Initializing service..." />
                          </div>
                        )}
                      </Protected>
                    }
                  />
                  <Route
                    path="/issuers"
                    element={
                      <Protected roles={role}>
                        {blockchainService ? (
                          <TrustedIssuersPage service={blockchainService} />
                        ) : (
                          <div className="flex justify-center items-center h-full">
                            <Spin tip="Initializing service..." />
                          </div>
                        )}
                      </Protected>
                    }
                  />
                  <Route
                    path="/issuers/create"
                    element={
                      <Protected roles={role}>
                        {blockchainService ? (
                          <CreateTrustedIssuer service={blockchainService} />
                        ) : (
                          <div className="flex justify-center items-center h-full">
                            <Spin tip="Initializing service..." />
                          </div>
                        )}
                      </Protected>
                    }
                  />
                  <Route
                    path="/issuers/:issuerId"
                    element={
                      <Protected roles={role}>
                        {blockchainService ? (
                          <CreateTrustedIssuer service={blockchainService} />
                        ) : (
                          <div className="flex justify-center items-center h-full">
                            <Spin tip="Initializing service..." />
                          </div>
                        )}
                      </Protected>
                    }
                  />
                  <Route
                    path="/identities"
                    element={
                      <Protected roles={role}>
                        {blockchainService ? (
                          <IdentitiesPage service={blockchainService} />
                        ) : (
                          <div className="flex justify-center items-center h-full">
                            <Spin tip="Initializing service..." />
                          </div>
                        )}
                      </Protected>
                    }
                  />
                  <Route
                    path="/identities/create"
                    element={
                      <Protected roles={role}>
                        {blockchainService ? (
                          <CreateDigitalId service={blockchainService} />
                        ) : (
                          <div className="flex justify-center items-center h-full">
                            <Spin tip="Initializing service..." />
                          </div>
                        )}
                      </Protected>
                    }
                  />
                  <Route
                    path="/identities/:identityId/:userId?"
                    element={
                      <Protected roles={role}>
                        {blockchainService ? (
                          <DigitalIdentityDetailView service={blockchainService} />
                        ) : (
                          <div className="flex justify-center items-center h-full">
                            <Spin tip="Initializing service..." />
                          </div>
                        )}
                      </Protected>
                    }
                  />
                  <Route
                    path="/identities/:identityId/edit"
                    element={
                      <Protected roles={role}>
                        {blockchainService ? (
                          <EditClaims service={blockchainService} />
                        ) : (
                          <div className="flex justify-center items-center h-full">
                            <Spin tip="Initializing service..." />
                          </div>
                        )}
                      </Protected>
                    }
                  />
                  <Route
                    path="/identities/:identity/edit/summary"
                    element={
                      <Protected roles={role}>
                        {blockchainService ? (
                          <EditClaimsSummaryView service={blockchainService} />
                        ) : (
                          <div className="flex justify-center items-center h-full">
                            <Spin tip="Initializing service..." />
                          </div>
                        )}
                      </Protected>
                    }
                  />
                </Routes>
              </div>
            </Layout>
          </Router>
        </RoleContext.Provider>
      </RainbowKitProvider>
    </WagmiConfig>
  );
}

export default App;
